import { KnowledgeBaseDocument, KnowledgeSource, KnowledgeSourceStatus, WebSource } from '../../types';
import { hostOf } from '../../services/webSources';

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/** Compact relative time for the list's right-hand column. */
export function formatRelative(iso?: string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

/** Elapsed time for a running job, so "Indexing" never looks like a hang. */
export function formatElapsed(sinceIso?: string): string {
  if (!sinceIso) return '';
  const started = new Date(sinceIso).getTime();
  if (Number.isNaN(started)) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - started) / 1000));
  const minutes = Math.floor(seconds / 60);
  return minutes > 0 ? `${minutes}m ${seconds % 60}s` : `${seconds}s`;
}

const DOCUMENT_STATUS: Record<string, KnowledgeSourceStatus> = {
  uploading: 'uploading',
  uploaded: 'not_indexed',
  processing: 'indexing',
  ready: 'indexed',
  error: 'failed',
};

// A website status with no entry here falls to 'not_indexed', which reads as "we
// have not got to it yet" — so a state that is actually terminal MUST be listed.
// `rejected` fell through this table for the whole of its first life, and a refused
// crawl rendered as a site sitting there apparently untouched, with no error and no
// reason anywhere on the screen.
const WEBSITE_STATUS: Record<string, KnowledgeSourceStatus> = {
  pending: 'not_indexed',
  crawling: 'indexing',
  indexed: 'indexed',
  error: 'failed',
  rejected: 'rejected',
};

/**
 * Flatten documents and websites into the one list the panel renders.
 * `pendingDeletion` wins over everything — a staged removal reads as staged
 * regardless of what the backend still reports.
 */
export function toSources(
  documents: KnowledgeBaseDocument[],
  websites: WebSource[],
  pendingDeletion: Set<string>,
  uploadProgress: Record<string, number>
): KnowledgeSource[] {
  const fileSources: KnowledgeSource[] = documents.map(doc => ({
    id: doc.id,
    kind: 'file',
    name: doc.name,
    detail: doc.contentType,
    size: doc.size,
    status: pendingDeletion.has(doc.id) ? 'removing' : (DOCUMENT_STATUS[doc.status] || 'not_indexed'),
    updatedAt: doc.uploadedAt,
    progress: uploadProgress[doc.id],
    document: doc,
  }));

  const webSources: KnowledgeSource[] = websites.map(site => ({
    id: site.id,
    kind: 'website',
    name: hostOf(site.config.url),
    detail: site.config.url,
    // The trailing `?? undefined` is load-bearing (C12). The API sends BOTH counts
    // as null until a crawl records them, `??` passes null straight through, and
    // SourceRow's guard tests for undefined — so the row rendered "null pages" for
    // the whole of every crawl.
    pages: site.pagesIndexed ?? site.pagesDiscovered ?? undefined,
    // `checkingSize` OVERRIDES the table rather than being a row in it (C13). The
    // backend status during that window is `indexed` — truthfully, the previous copy
    // is live — so a lookup keyed on status can never see it. Order matters: a staged
    // deletion still wins over both.
    status: pendingDeletion.has(site.id)
      ? 'removing'
      : site.checkingSize
        ? 'checking'
        : (WEBSITE_STATUS[site.status] || 'not_indexed'),
    updatedAt: site.lastCrawledAt || site.addedAt,
    errorMessage: site.errorMessage,
    website: site,
  }));

  return [...fileSources, ...webSources];
}
